import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { AssignmentSubmissionStatus } from "./useAssignments";

export interface ModuleRequirements {
  // Videos
  totalVideos: number;
  // Quizzes
  totalQuizzes: number;
  passedQuizzes: number;
  /**
   * True when THIS module opts in to requiring homework
   * (modules.requires_homework). Defaults to false.
   *
   * This used to be derived from a COUNT of homework_submissions for the module
   * with no user filter, so it flipped true as soon as ANY learner submitted —
   * which is why homework could not safely be used for gating.
   */
  hasHomework: boolean;
  /** True when THIS learner has a homework submission for the module. */
  homeworkSubmitted: boolean;
  // Assignments (total)
  totalAssignments: number;
  completedAssignments: number;
  // Required Assignments (must be passed)
  requiredAssignments: number;
  passedRequiredAssignments: number;
  pendingRequiredAssignments: { id: string; title: string; status: AssignmentSubmissionStatus | null }[];
  // Overall
  isComplete: boolean;
  missingRequirements: string[];
}

export function useModuleRequirements(moduleId: string | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["module-requirements", moduleId, user?.id],
    queryFn: async (): Promise<ModuleRequirements> => {
      if (!moduleId || !user?.id) {
        return {
          totalVideos: 0,
          totalQuizzes: 0,
          passedQuizzes: 0,
          hasHomework: false,
          homeworkSubmitted: false,
          totalAssignments: 0,
          completedAssignments: 0,
          requiredAssignments: 0,
          passedRequiredAssignments: 0,
          pendingRequiredAssignments: [],
          isComplete: true,
          missingRequirements: [],
        };
      }

      const missingRequirements: string[] = [];

      // Check videos in module content
      const { data: content } = await supabase
        .from("module_content")
        .select("id, content_type")
        .eq("module_id", moduleId);

      const totalVideos = content?.filter((c) => c.content_type === "video").length || 0;

      // Check published quizzes for this module
      const { data: quizzes } = await supabase
        .from("quizzes")
        .select("id, title")
        .eq("module_id", moduleId)
        .eq("is_published", true);

      const totalQuizzes = quizzes?.length || 0;
      let passedQuizzes = 0;

      if (quizzes && quizzes.length > 0) {
        // Check which quizzes the user has passed
        for (const quiz of quizzes) {
          const { data: attempts } = await supabase
            .from("quiz_attempts")
            .select("passed")
            .eq("quiz_id", quiz.id)
            .eq("user_id", user.id)
            .eq("passed", true)
            .limit(1);

          if (attempts && attempts.length > 0) {
            passedQuizzes++;
          } else {
            missingRequirements.push(`Pass quiz: "${quiz.title}"`);
          }
        }
      }

      // Check homework submissions (legacy system)
      const { data: homeworkSubmission } = await supabase
        .from("homework_submissions")
        .select("id")
        .eq("module_id", moduleId)
        .eq("user_id", user.id)
        .maybeSingle();

      // Opt-in flag on the module itself. NOT inferred from other learners'
      // submissions (see the interface comment above).
      const { data: moduleRow } = await supabase
        .from("modules")
        .select("requires_homework")
        .eq("id", moduleId)
        .maybeSingle();

      const hasHomework =
        (moduleRow as { requires_homework?: boolean } | null)?.requires_homework === true;
      const homeworkSubmitted = !!homeworkSubmission;

      // Gate completion ONLY when the module explicitly requires homework.
      if (hasHomework && !homeworkSubmitted) {
        missingRequirements.push("Submit homework for this module");
      }

      // Check published assignments for this module
      const { data: assignments } = await supabase
        .from("assignments")
        .select("id, title, required, completion_requirement")
        .eq("module_id", moduleId)
        .eq("is_published", true);

      const totalAssignments = assignments?.length || 0;
      let completedAssignments = 0;
      let requiredAssignments = 0;
      let passedRequiredAssignments = 0;
      const pendingRequiredAssignments: { id: string; title: string; status: AssignmentSubmissionStatus | null }[] = [];

      if (assignments && assignments.length > 0) {
        // Get all submissions for this user for these assignments
        const assignmentIds = assignments.map(a => a.id);
        const { data: submissions } = await supabase
          .from("assignment_submissions")
          .select("assignment_id, status")
          .eq("user_id", user.id)
          .in("assignment_id", assignmentIds);

        const submissionMap = new Map<string, AssignmentSubmissionStatus>(
          submissions?.map((s: { assignment_id: string; status: string }) =>
            [s.assignment_id, s.status as AssignmentSubmissionStatus]
          ) || []
        );

        for (const assignment of assignments) {
          const status: AssignmentSubmissionStatus | null = submissionMap.get(assignment.id) ?? null;
          const tier = (assignment as { completion_requirement?: string }).completion_requirement
            ?? (assignment.required ? "review_required" : "optional");

          // Count completed (any submission)
          if (status) {
            completedAssignments++;
          }

          if (tier === "optional") {
            // Never blocks; nothing else to track.
            continue;
          }

          // Both gating tiers count as "required" for progress accounting.
          requiredAssignments++;

          const satisfied =
            tier === "review_required"
              ? status === "passed"
              : status === "submitted" || status === "passed" || status === "retry";

          if (satisfied) {
            passedRequiredAssignments++;
            continue;
          }

          pendingRequiredAssignments.push({
            id: assignment.id,
            title: assignment.title,
            status,
          });

          if (tier === "review_required") {
            if (!status) {
              missingRequirements.push(`Complete required assignment: "${assignment.title}"`);
            } else if (status === "submitted") {
              missingRequirements.push(`Awaiting review: "${assignment.title}"`);
            } else if (status === "retry") {
              missingRequirements.push(`Modify assignment: "${assignment.title}"`);
            } else if (status === "failed") {
              missingRequirements.push(`Assignment failed: "${assignment.title}"`);
            }
          } else {
            // submission_required — only unsatisfied when nothing submitted or failed
            if (!status) {
              missingRequirements.push(`Submit required assignment: "${assignment.title}"`);
            } else if (status === "failed") {
              missingRequirements.push(`Resubmit assignment: "${assignment.title}"`);
            }
          }
        }
      }

      const isComplete = missingRequirements.length === 0;

      return {
        totalVideos,
        totalQuizzes,
        passedQuizzes,
        hasHomework,
        homeworkSubmitted,
        totalAssignments,
        completedAssignments,
        requiredAssignments,
        passedRequiredAssignments,
        pendingRequiredAssignments,
        isComplete,
        missingRequirements,
      };
    },
    enabled: !!moduleId && !!user?.id,
  });
}

/**
 * Check if user can progress from a specific module
 * Returns true only if all REQUIRED assignments are PASSED
 * (Optional assignments only need to be submitted)
 */
export function useCanProgressFromModule(moduleId: string | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["can-progress-from-module", moduleId, user?.id],
    queryFn: async (): Promise<{
      canProgress: boolean;
      reason?: string;
      missingAssignments: { id: string; title: string; status: AssignmentSubmissionStatus | null }[];
    }> => {
      if (!moduleId || !user?.id) {
        return { canProgress: true, missingAssignments: [] };
      }

      // Get all assignments in this module that gate progression (not optional)
      const { data: assignments, error: assignmentsError } = await supabase
        .from("assignments")
        .select("id, title, required, completion_requirement")
        .eq("module_id", moduleId)
        .eq("is_published", true)
        .neq("completion_requirement", "optional");

      if (assignmentsError) {
        console.error("[useCanProgressFromModule] Error fetching assignments:", assignmentsError);
        return { canProgress: false, reason: "Error checking assignments", missingAssignments: [] };
      }

      if (!assignments || assignments.length === 0) {
        return { canProgress: true, missingAssignments: [] };
      }

      // Get user's submissions for these assignments
      const assignmentIds = assignments.map(a => a.id);
      const { data: submissions, error: submissionsError } = await supabase
        .from("assignment_submissions")
        .select("assignment_id, status")
        .eq("user_id", user.id)
        .in("assignment_id", assignmentIds);

      if (submissionsError) {
        console.error("[useCanProgressFromModule] Error fetching submissions:", submissionsError);
        return { canProgress: false, reason: "Error checking submissions", missingAssignments: [] };
      }

      const statusMap = new Map<string, AssignmentSubmissionStatus>(
        submissions?.map((s: { assignment_id: string; status: string }) =>
          [s.assignment_id, s.status as AssignmentSubmissionStatus]
        ) || []
      );

      const missingAssignments: { id: string; title: string; status: AssignmentSubmissionStatus | null }[] = [];

      for (const assignment of assignments) {
        const status: AssignmentSubmissionStatus | null = statusMap.get(assignment.id) ?? null;
        const tier = (assignment as { completion_requirement?: string }).completion_requirement
          ?? (assignment.required ? "review_required" : "optional");

        const satisfied =
          tier === "review_required"
            ? status === "passed"
            : tier === "submission_required"
              ? status === "submitted" || status === "passed" || status === "retry"
              : true;

        if (!satisfied) {
          missingAssignments.push({ id: assignment.id, title: assignment.title, status });
        }
      }

      if (missingAssignments.length > 0) {
        return {
          canProgress: false,
          reason: "You must satisfy all required assignments before moving to the next module.",
          missingAssignments,
        };
      }

      return { canProgress: true, missingAssignments: [] };
    },
    enabled: !!moduleId && !!user?.id,
  });
}
